import * as React from 'react';
import { BaseComponent } from './BaseComponent';
import { DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';

export type RelativePosition = 'top' | 'bottom' | 'left' | 'right';

export interface CoreTooltipProps extends React.Props<CoreTooltip> {
    config: {label: string, position?: RelativePosition, forceLabel?: string, posFix?: number};
}

export interface CoreTooltipPosition {
    x: number;
    y: number;
    position: RelativePosition;
}

export class CoreTooltip extends BaseComponent<CoreTooltipProps, CoreTooltipPosition> {
    private static maxLettersPerLine: number = 15;
    private static Margin: number = 5;

    protected x: number = 0;
    protected y: number = 0;

    private domElement: HTMLDivElement;
    private parentClientRect: ClientRect;

    public componentWillMount(): void {
        this.setState({x: 0, y: 0, position: 'top'});
    }

    public componentDidMount(): void {
        this.updatePosition();
        window.addEventListener('resize', this.updatePosition);
        this.addActionListener((action: DispatcherBaseAction): void => {
            if (
                action.actionType === ActionConstants.HIDE_BANNER ||
                action.actionType === ActionConstants.UPDATE_TOOLTIPS
            ) {
                this.updatePosition();
            }
        });
        if (this.domElement != null && this.domElement.parentNode != null) {
            this.domElement.parentNode.addEventListener('mouseover', this.updatePosition);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        window.removeEventListener('resize', this.updatePosition);
        if (this.domElement != null && this.domElement.parentNode != null) {
            this.domElement.parentNode.removeEventListener('mouseover', this.updatePosition);
        }
    }

    public updatePosition = (): void => {
        setTimeout(
            () => {
                if (this.domElement != null && this.domElement.parentNode != null) {
                    const clientRect: ClientRect = (this.domElement.parentNode as HTMLElement).getBoundingClientRect();
                    if (this.parentClientRect === clientRect) {
                        return;
                    }
                    this.parentClientRect = clientRect;
                    let position: RelativePosition | undefined = this.props.config.position;
                    if (position == null) {
                        position = 'bottom';
                        if ((clientRect.bottom + 100) > document.body.clientHeight) {
                            position = 'top';
                        } else if ((clientRect.right + 100) > document.body.clientWidth) {
                            position = 'left';
                        } else if ((clientRect.left - 100) < 0) {
                            position = 'right';
                        }
                    }
                    if (position === 'top') {
                        this.setState({
                            x: clientRect.left + .5 * clientRect.width,
                            y: clientRect.top - CoreTooltip.Margin,
                            position: 'top'
                        });
                    } else if (position === 'left') {
                        this.setState({
                            x: clientRect.left - CoreTooltip.Margin,
                            y: clientRect.top + .5 * clientRect.height,
                            position: 'left'
                        });
                    } else if (position === 'right') {
                        this.setState({
                            x: clientRect.right + CoreTooltip.Margin,
                            y: clientRect.top + .5 * clientRect.height,
                            position: 'right'
                        });
                    } else {
                        this.setState({
                            x: clientRect.left + .5 * clientRect.width,
                            y: clientRect.bottom + CoreTooltip.Margin,
                            position: 'bottom'
                        });
                    }
                }
            },
            250
        );
    };

    public render(): JSX.Element {
        return (
            <div
                className={'core-tooltip' + (' core-tooltip-' + this.state.position) + (this.props.config.forceLabel != null ? ' core-tooltip-show' : '')}
                style={this.getStyle()}
                ref={(node: HTMLDivElement) => { this.domElement = node; }}
            >
                {this.labelLines(this.props.config.forceLabel ? this.props.config.forceLabel : this.props.config.label)}
            </div>
        );
    }

    protected labelLines(label: string): Array<JSX.Element> | null {
        if (label === undefined) {
            return null;
        }

        if (label.length <= CoreTooltip.maxLettersPerLine) {
            return [<span key="0">{label}</span>];
        }

        const result: Array<JSX.Element> = [];

        let currentString: string = '';

        label.split(' ').map((labelPart, index, allWords) => {
            currentString += labelPart + ((index < (allWords.length - 1) ) ? ' ' : '');
            if (currentString.length >= CoreTooltip.maxLettersPerLine) {
                result.push(<div className="core-tooltip-label" key={index}>{currentString}</div>);
                currentString = '';
            }
        });

        if (currentString.length > 0) {
            result.push(<div className="core-tooltip-label" key="last">{currentString}</div>);
        }

        return result;
    }

    protected getStyle(): React.CSSProperties {
        const style: React.CSSProperties = {};
        style.marginLeft = (this.props.config.posFix != null ? this.props.config.posFix : 0);
        switch (this.state.position) {
            case 'left':
                style.left = 'auto';
                style.bottom = 'auto';
                style.right = 'calc(100% - ' + this.state.x + 'px)';
                style.top = this.state.y;
                break;
            case 'right':
                style.bottom = 'auto';
                style.right = 'auto';
                style.left = this.state.x;
                style.top = this.state.y;
                break;
            case 'top':
                style.bottom = 'auto';
                style.right = 'auto';
                style.left = this.state.x;
                style.top = this.state.y;
                break;
            case 'bottom':
                style.bottom = 'auto';
                style.right = 'auto';
                style.left = this.state.x;
                style.top = this.state.y;
                break;
            default:
                break;
        }
        return style;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/CoreTooltip.tsx